import { Box, Button, Theme } from "@outschool/backpack";
import { ParentFragmentFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { gql, useQuery } from "@outschool/ui-apollo";
import {
  LeaderFragment,
  PublicReviewQuote,
} from "@outschool/ui-components-website";
import { Screen } from "@outschool/ui-utils";
import React, { ReactNode } from "react";

import { WrittenReviews } from "../categories/curated/CuratedReviews";
import { AlwaysVisibleDisclaimerText } from "../ParentProfile/PrivacyDisclaimerText";

const textFeedbackByUserQuery = gql`
  query TextFeedbackByUserQuery($userUid: ID!, $offset: Int) {
    textFeedbackByUser(userUid: $userUid, offset: $offset) {
      uid
      details {
        publicReview
      }
      activity_star_rating
      created_at
      activity {
        uid
        isClub
        slug_id
        title
        titleTranslated
        leader {
          ...LeaderFragment
        }
      }
    }
  }
  ${LeaderFragment}
`;

export default function WrittenReviewCarousel({
  title,
  parent,
  isReviewModalOpen,
  setIsReviewModalOpen,
  isCurrentUsersProfile,
}: {
  title: ReactNode;
  parent: ParentFragmentFragment;
  isReviewModalOpen: boolean;
  setIsReviewModalOpen: (val: boolean) => void;
  isCurrentUsersProfile: boolean;
}) {
  const { t } = useTranslation("client\\routes\\ParentProfile");
  const isMobile = Screen.useIsMobile();
  const { data, error, loading } = useQuery(textFeedbackByUserQuery, {
    variables: { userUid: parent.uid, offset: 0 },
  });

  if (!data || loading || error) {
    return null;
  }

  if (!data.textFeedbackByUser.length) {
    return null;
  }

  const showButton = data.textFeedbackByUser.length > 4;

  return (
    <Box
      sx={(theme: Theme) => ({
        mx: "1em",
        mb: "1rem",
        mt: "1em",
        paddingBottom: "64px",
        [theme.breakpoints.down("sm")]: { mx: 0 },
      })}
    >
      <Box flex sx={{ flexDirection: "column" }}>
        {title}
        {isCurrentUsersProfile && <AlwaysVisibleDisclaimerText />}
        {showButton && (
          <Button
            sx={{ maxWidth: 200, my: 5 }}
            onClick={() => {
              setIsReviewModalOpen(!isReviewModalOpen);
            }}
          >
            {t("See All Reviews")}
          </Button>
        )}
      </Box>
      {isMobile ? (
        <Box sx={{ marginTop: "1em" }}>
          <WrittenReviews
            reviews={data.textFeedbackByUser}
            reviewOverrideLimit={4}
          />
        </Box>
      ) : (
        <Box
          flex
          sx={(theme: $TSFixMe) => ({
            flexWrap: "wrap",
            width: "100%",
            flexDirection: "column",

            [theme.breakpoints.up("lg")]: {
              flexDirection: "row",
            },
          })}
        >
          {data.textFeedbackByUser
            .slice(0, 4)
            .map((review: $TSFixMe, idx: $TSFixMe) => (
              <PublicReviewQuote
                containerSx={theme => ({
                  paddingY: "0.5em",
                  paddingRight: idx % 2 === 0 ? "0.5em" : "0px",
                  paddingLeft: idx % 2 !== 0 ? "0.5em" : "0px",
                  width: "100%",
                  [theme.breakpoints.up("lg")]: {
                    width: "50%",
                  },
                })}
                reviewSx={{ minHeight: "237px" }}
                showMoreMaxChars={200}
                teacher={review.activity.leader}
                activity={review.activity}
                review={{ ...review, parent: parent }}
                key={review.uid + "/quote"}
                trackingLabel="public_review_of_leader"
              />
            ))}
        </Box>
      )}
    </Box>
  );
}
